import { useCallback, useRef } from 'react';
import first from 'lodash/first';
import { useMutation } from '@apollo/react-hooks';
import type { ApolloError } from 'apollo-client';
// eslint-disable-next-line jira/restricted/date-fns
import dateFormat from 'date-fns/format';
import parseISO from 'date-fns/parseISO';
import {
	MutationError,
	isAbortError,
	AbortError,
	DEFAULT_ABORT_ERROR_MESSAGE,
} from '@atlassian/jira-business-error-handling';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import { ff } from '@atlassian/jira-feature-flagging';
import { ValidationError, FetchError } from '@atlassian/jira-fetch';
import { DateOnly } from '@atlassian/jira-platform-utils-date-only';
import {
	fireOperationalAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { useStartDateField } from '@atlassian/jira-router-resources-business-configuration';
import { useCloudId } from '@atlassian/jira-tenant-context-controller';
import type { UpdateIssueField } from '../get-timeline-view-issues/types';
import type {
	ScheduleTimelineJwmItem,
	ScheduleTimelineJwmItemVariables,
} from './__generated_apollo__/ScheduleTimelineJwmItem';
import { SCHEDULE_TIMELINE_ITEM_MUTATION } from './gql';

export interface ExecutionResult<T> {
	data?: T;
	reason?: string;
}

type ReduceAccumulator = {
	success: boolean;
	aborted: boolean;
	errorMessage: string | null;
};

type OnScheduleItemFunction = (
	item: {
		issueId: string;
		startDate?: number;
		dueDate?: number;
		isDragOperation: boolean;
	},
	descendantIssueIds?: string[],
) => Promise<boolean | undefined>;

const DATE_FORMAT = 'dd/MMM/yyyy';
const QUERY_NAME = 'scheduleTimelineJwmItem';

const mapDateToValidDateVariable = (date: number | string) => {
	const mappedDate = DateOnly.fromDateAccordingToUTC(new Date(date)).toISOString();
	return dateFormat(parseISO(mappedDate), DATE_FORMAT);
};

export class NotFoundError extends ValidationError {
	constructor() {
		super('Issue not found');
	}
}

export const useScheduleItem = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const cloudId = useCloudId();
	const { data: startDateField } = useStartDateField();
	const abortController = useRef<AbortController>(new AbortController());
	// TODO: Remove during `jwm-schedule-timeline-update-all-descendants-on-drag-operation_v39wp` ff cleanup
	const abortControllerMapRef = useRef<Map<string, AbortController>>(new Map());

	const doFireErrorAnalytics = (error: ApolloError | MutationError[]) => {
		fireErrorAnalytics({
			meta: {
				id: QUERY_NAME,
				packageName: 'jiraBusinessTimeline',
				teamName: 'jira-yurree',
			},
			error: Array.isArray(error) ? error[0] : error,
			attributes: { message: 'Failed to schedule item' },
			sendToPrivacyUnsafeSplunk: true,
		});
	};

	const [scheduleItem] = useMutation<ScheduleTimelineJwmItem, ScheduleTimelineJwmItemVariables>(
		SCHEDULE_TIMELINE_ITEM_MUTATION,
		{
			onError: (apolloError) => {
				if (ff('jwm.visual-hierarchy-milestone-1') && isAbortError(apolloError.networkError)) {
					throw apolloError.networkError;
				}

				const firstError = first(apolloError?.graphQLErrors);

				const errorCode = firstError?.extensions?.statusCode;
				if (ff('jwm.filter-4xx-from-timeline-schedule-issue') && errorCode >= 500) {
					doFireErrorAnalytics(apolloError);
				} else if (!ff('jwm.filter-4xx-from-timeline-schedule-issue')) {
					doFireErrorAnalytics(apolloError);
				}

				throw new FetchError(
					errorCode || 500,
					firstError?.message || 'Timeline schedule item mutation returned an error',
				);
			},
			onCompleted: (onCompletedData) => {
				fireOperationalAnalytics(createAnalyticsEvent({}), `${QUERY_NAME} succeeded`, {});
				if (onCompletedData.scheduleTimelineJwmItem?.errors?.length) {
					doFireErrorAnalytics(
						onCompletedData.scheduleTimelineJwmItem.errors.map(
							(e) => new MutationError({ message: e.message }),
						),
					);
				} else if (onCompletedData?.scheduleTimelineJwmItem?.success === false) {
					doFireErrorAnalytics([
						new MutationError({
							message: 'Success mutation value as false without error',
						}),
					]);
				}
			},
		},
	);

	const initialiseAbortController = useCallback((id: string) => {
		const localAbortController = new AbortController();
		abortControllerMapRef.current.set(id, localAbortController);
		return localAbortController;
	}, []);

	const abortExistingControllers = useCallback((issueIds: string[]) => {
		issueIds.forEach((issueId) => {
			const controller = abortControllerMapRef.current.get(issueId);
			if (controller) {
				controller.abort(new AbortError(DEFAULT_ABORT_ERROR_MESSAGE));
				abortControllerMapRef.current.delete(issueId);
			}
		});
	}, []);

	/**
	 * This function is used to schedule multiple items at once. It takes an array of `UpdateIssueField` objects and a boolean indicating whether the operation is a drag operation.
	 * Each `UpdateIssueField` object should contain the issue ID and the new start and due dates.
	 * The function will return a promise that resolves to a boolean indicating the success of the operation.
	 *
	 * Note: This function will be removed during the cleanup of the `jwm-schedule-timeline-update-all-descendants-on-drag-operation_v39wp` feature flag.
	 *
	 * @param updateIssueFields - An array of objects, each containing the issue ID and the new start and due dates.
	 * @param isDragOperation - A boolean indicating whether the operation is a drag operation.
	 * @returns A promise that resolves to a boolean indicating the success of the operation.
	 */
	const onScheduleItems = useCallback(
		async (updateIssueFields: UpdateIssueField[], isDragOperation: boolean) => {
			const mutationPromises = updateIssueFields.map((item) => {
				const localAbortController = initialiseAbortController(item.id);
				const variables = {
					input: {
						issueId: `ari:cloud:jira:${cloudId}:issue/${item.id}`,
						operation: isDragOperation ? 'DRAG' : 'SET',
						...(item.fields.startDate != null && startDateField?.id
							? {
									startDate: {
										fieldId: startDateField.id,
										date: {
											formattedDate: mapDateToValidDateVariable(item.fields.startDate),
										},
									},
								}
							: {}),
						...(item.fields.dueDate != null
							? {
									dueDate: {
										fieldId: 'duedate',
										date: {
											formattedDate: mapDateToValidDateVariable(item.fields.dueDate),
										},
									},
								}
							: {}),
					},
				};

				return scheduleItem({
					// @ts-expect-error - TS2305 -  Type 'string' is not assignable to type 'JWMScheduleTimelineItemOperation'.
					variables,
					context: {
						fetchOptions: {
							signal: localAbortController.signal,
						},
					},
				});
			});

			const results = await Promise.allSettled(mutationPromises);

			const { aborted, success, errorMessage } = results.reduce<ReduceAccumulator>(
				(acc, result) => {
					if (result.status === 'rejected') {
						acc.errorMessage =
							result.reason instanceof Error ? result.reason.message : result.reason;

						if (!acc.aborted) {
							acc.aborted = result.reason instanceof AbortError;
						}

						acc.success = false;
					} else {
						const error = first(result.value.data?.scheduleTimelineJwmItem?.errors);
						const isSuccessful = Boolean(result.value?.data?.scheduleTimelineJwmItem?.success);

						if (error || !isSuccessful) {
							acc.errorMessage =
								error?.message ||
								'Timeline schedule item mutation result data included an error or success was not true';
							acc.success = false;
						}
					}

					return acc;
				},
				{ aborted: false, success: true, errorMessage: null },
			);

			if (!success && !aborted) {
				throw new Error(
					errorMessage ||
						'Timeline schedule item mutation result data included an error or success was not true',
				);
			}

			return success && !aborted;
		},
		[initialiseAbortController, cloudId, startDateField, scheduleItem],
	);

	const onScheduleItem: OnScheduleItemFunction = useCallback(
		({ issueId, startDate, dueDate, isDragOperation }, descendantIssueIds = []) => {
			if (ff('jwm-schedule-timeline-update-all-descendants-on-drag-operation_v39wp')) {
				abortController.current.abort(new AbortError(DEFAULT_ABORT_ERROR_MESSAGE));
				abortController.current = new AbortController();
			} else if (ff('jwm.visual-hierarchy-milestone-1')) {
				abortExistingControllers([issueId, ...descendantIssueIds]);
			}

			const variables = {
				input: {
					issueId: `ari:cloud:jira:${cloudId}:issue/${issueId}`,
					operation: isDragOperation ? 'DRAG' : 'SET',
					...(startDate != null && startDateField?.id
						? {
								startDate: {
									fieldId: startDateField.id,
									date: {
										formattedDate: mapDateToValidDateVariable(startDate),
									},
								},
							}
						: {}),
					...(dueDate != null
						? {
								dueDate: {
									fieldId: 'duedate',
									date: {
										formattedDate: mapDateToValidDateVariable(dueDate),
									},
								},
							}
						: {}),
				},
			};

			return scheduleItem({
				// @ts-expect-error - TS2305 -  Type 'string' is not assignable to type 'JWMScheduleTimelineItemOperation'.
				variables,
				...(ff('jwm.visual-hierarchy-milestone-1')
					? {
							context: {
								fetchOptions: {
									signal: ff('jwm-schedule-timeline-update-all-descendants-on-drag-operation_v39wp')
										? abortController.current.signal
										: initialiseAbortController(issueId).signal,
								},
							},
						}
					: {}),
			}).then((result) => {
				const error = first(result?.data?.scheduleTimelineJwmItem?.errors);

				if (error || result?.data?.scheduleTimelineJwmItem?.success !== true) {
					throw new Error(
						error?.message ||
							'Timeline schedule item mutation result data included an error or success was not true',
					);
				}

				return result?.data?.scheduleTimelineJwmItem?.success;
			});
		},
		[cloudId, startDateField, scheduleItem, initialiseAbortController, abortExistingControllers],
	);

	return {
		onScheduleItem,
		onScheduleItems,
	};
};
